/**
 * Growth Dashboard — admin-only funil de aquisição.
 * Route: /settings/growth
 *
 * KPIs, funil, série temporal, atribuição, coortes semanais e leaderboard de insights.
 */
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import {
  TrendingUp, RefreshCw, Loader2, ExternalLink, Send, Eye, Users, Award,
  ArrowUpRight, ArrowDownRight, Sparkles, Filter, Zap, MousePointerClick,
} from 'lucide-react';
import Layout from '../../components/Layout';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';

function KpiCard({ label, value, hint, icon: Icon, tone = 'indigo', testid }) {
  const tones = {
    indigo: 'from-indigo-500 to-indigo-600',
    emerald: 'from-emerald-500 to-emerald-600',
    amber: 'from-amber-500 to-amber-600',
    sky: 'from-sky-500 to-sky-600',
  };
  return (
    <Card data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</span>
          <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${tones[tone]} flex items-center justify-center`}>
            <Icon className="h-4 w-4 text-white" />
          </div>
        </div>
        <div className="text-3xl font-bold text-slate-900 tabular-nums">{value}</div>
        {hint && <div className="text-xs text-slate-500 mt-2">{hint}</div>}
      </CardContent>
    </Card>
  );
}

function TimeseriesChart({ series }) {
  if (!series?.length) return <div className="text-sm text-slate-500 py-8 text-center">Sem dados no período.</div>;
  const maxPv = Math.max(1, ...series.map(s => s.pageviews));
  const maxLd = Math.max(1, ...series.map(s => s.leads));
  const w = 720, h = 220, pad = 28;
  const barW = (w - pad * 2) / series.length;

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-56" data-testid="growth-timeseries-chart">
        {/* Grid */}
        {[0, 0.25, 0.5, 0.75, 1].map((t, i) => (
          <line key={i} x1={pad} x2={w - pad} y1={h - pad - (h - pad * 2) * t} y2={h - pad - (h - pad * 2) * t} stroke="#e2e8f0" strokeWidth="1" />
        ))}
        {/* Bars: pageviews */}
        {series.map((s, i) => {
          const bh = ((s.pageviews / maxPv) * (h - pad * 2)) || 0;
          const x = pad + i * barW + 2;
          return <rect key={`pv-${i}`} x={x} y={h - pad - bh} width={Math.max(1, barW - 4)} height={bh} fill="#6366f1" opacity="0.55" rx="2" />;
        })}
        {/* Line: leads */}
        <polyline
          fill="none"
          stroke="#10b981"
          strokeWidth="2.5"
          points={series.map((s, i) => {
            const x = pad + i * barW + barW / 2;
            const y = h - pad - ((s.leads / maxLd) * (h - pad * 2));
            return `${x},${y}`;
          }).join(' ')}
        />
        {series.map((s, i) => {
          const x = pad + i * barW + barW / 2;
          const y = h - pad - ((s.leads / maxLd) * (h - pad * 2));
          return <circle key={`ld-${i}`} cx={x} cy={y} r="3" fill="#10b981" />;
        })}
      </svg>
      <div className="flex items-center justify-center gap-6 mt-2 text-xs text-slate-600">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-indigo-500 opacity-60" />Pageviews</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-1 rounded bg-emerald-500" />Leads</span>
      </div>
    </div>
  );
}

function FunnelStep({ step, index, isLast }) {
  const width = Math.max(20, Math.min(100, step.rate_from_previous ?? 100));
  return (
    <div className="flex items-center gap-3" data-testid={`funnel-step-${step.key}`}>
      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-semibold text-sm">{index + 1}</div>
      <div className="flex-1">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-slate-700">{step.label}</span>
          <span className="text-lg font-bold text-slate-900 tabular-nums">{step.count.toLocaleString('pt-PT')}</span>
        </div>
        <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-indigo-500 to-indigo-600" style={{ width: `${width}%` }} />
        </div>
        {step.rate_from_previous !== null && !isLast && (
          <div className="text-xs text-slate-500 mt-1">{step.rate_from_previous}% conversão do passo anterior</div>
        )}
      </div>
    </div>
  );
}

export default function GrowthDashboard() {
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [funnel, setFunnel] = useState(null);
  const [timeseries, setTimeseries] = useState(null);
  const [attribution, setAttribution] = useState(null);
  const [cohorts, setCohorts] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [abTest, setAbTest] = useState(null);
  const [abLanding, setAbLanding] = useState('nis2');
  const [sending, setSending] = useState(false);

  useEffect(() => { document.title = 'Growth · NIS2AI'; }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, t, a, c, lb, ab] = await Promise.all([
        api.get(`/growth/funnel?days=${days}`),
        api.get(`/growth/timeseries?days=${Math.min(days, 90)}`),
        api.get(`/growth/attribution?days=${days}`),
        api.get('/growth/cohorts?weeks=8'),
        api.get(`/growth/insights-leaderboard?days=${days}`),
        api.get(`/growth/ab-test/${abLanding}?days=${days}&breakdown=true`),
      ]);
      setFunnel(f.data);
      setTimeseries(t.data);
      setAttribution(a.data);
      setCohorts(c.data);
      setLeaderboard(lb.data);
      setAbTest(ab.data);
    } catch (err) {
      toast.error('Falha ao carregar dados de growth.');
    } finally {
      setLoading(false);
    }
  }, [days, abLanding]);

  useEffect(() => { load(); }, [load]);

  const sendDigestNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post('/growth/digest/send-now');
      if (data.sent) toast.success(`Digest enviado a ${data.recipients} admins.`);
      else if (data.skipped) toast.warning(`Digest saltado: ${data.skipped}`);
      else if (data.error) toast.warning(`Digest não enviado: ${data.error}`);
      else toast.error('Falha ao enviar digest.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao enviar digest.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="growth-dashboard">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
              <TrendingUp className="h-6 w-6 text-indigo-600" /> Growth Dashboard
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Funil público, atribuição e coortes de leads das landings <span className="font-medium">/landing/*</span>, <span className="font-medium">/trust</span> e <span className="font-medium">/insights</span>.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
              <SelectTrigger className="w-40" data-testid="growth-range-select">
                <Filter className="h-3.5 w-3.5 mr-2" /> <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Últimos 7 dias</SelectItem>
                <SelectItem value="30">Últimos 30 dias</SelectItem>
                <SelectItem value="90">Últimos 90 dias</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline" size="sm"
              onClick={sendDigestNow} disabled={sending}
              data-testid="growth-send-digest-btn"
              title="Envia digest semanal por email agora aos admins"
            >
              {sending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />} Enviar digest agora
            </Button>
            <Button variant="ghost" size="sm" onClick={load} data-testid="growth-refresh-btn">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            label="Pageviews"
            value={(funnel?.steps?.[0]?.count ?? 0).toLocaleString('pt-PT')}
            hint={`total (todas paths): ${(funnel?.total_pageviews_all_paths ?? 0).toLocaleString('pt-PT')}`}
            icon={Eye}
            tone="indigo"
            testid="kpi-pageviews"
          />
          <KpiCard
            label="Leads"
            value={(funnel?.steps?.[1]?.count ?? 0).toLocaleString('pt-PT')}
            hint={`${funnel?.steps?.[1]?.rate_from_previous ?? 0}% de conversão`}
            icon={Users}
            tone="emerald"
            testid="kpi-leads"
          />
          <KpiCard
            label="Demo enviada"
            value={(funnel?.steps?.[2]?.count ?? 0).toLocaleString('pt-PT')}
            hint={`${funnel?.steps?.[2]?.rate_from_previous ?? 0}% dos leads`}
            icon={Send}
            tone="amber"
            testid="kpi-demo"
          />
          <KpiCard
            label="HubSpot sync"
            value={(funnel?.steps?.[3]?.count ?? 0).toLocaleString('pt-PT')}
            hint={`${funnel?.steps?.[3]?.rate_from_previous ?? 0}% dos leads`}
            icon={Zap}
            tone="sky"
            testid="kpi-hubspot"
          />
        </div>

        {/* Timeseries + Funnel */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-2" data-testid="growth-timeseries-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-indigo-600" /> Pageviews vs. Leads
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? <Loader2 className="h-6 w-6 animate-spin text-indigo-600 mx-auto my-16" /> :
                <TimeseriesChart series={timeseries?.series} />
              }
            </CardContent>
          </Card>

          <Card data-testid="growth-funnel-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <MousePointerClick className="h-4 w-4 text-indigo-600" /> Funil
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {loading ? <Loader2 className="h-6 w-6 animate-spin text-indigo-600 mx-auto my-16" /> :
                funnel?.steps?.map((s, i) => (
                  <FunnelStep key={s.key} step={s} index={i} isLast={i === funnel.steps.length - 1} />
                ))
              }
            </CardContent>
          </Card>
        </div>

        {/* Attribution */}
        <Tabs defaultValue="paths">
          <TabsList data-testid="growth-attribution-tabs">
            <TabsTrigger value="paths" data-testid="tab-paths">Top páginas</TabsTrigger>
            <TabsTrigger value="utm" data-testid="tab-utm">UTM Sources</TabsTrigger>
            <TabsTrigger value="referrers" data-testid="tab-referrers">Referrers</TabsTrigger>
            <TabsTrigger value="cohorts" data-testid="tab-cohorts">Coortes semanais</TabsTrigger>
          </TabsList>

          <TabsContent value="paths">
            <Card>
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-3">Path</th>
                      <th className="text-right px-4 py-3">Views</th>
                      <th className="text-right px-4 py-3">Sessões únicas</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(attribution?.top_paths || []).map((p) => (
                      <tr key={p.path} className="hover:bg-slate-50" data-testid={`path-row-${p.path.replace(/\//g, '-')}`}>
                        <td className="px-4 py-3 font-mono text-xs">{p.path}</td>
                        <td className="px-4 py-3 text-right font-semibold tabular-nums">{p.views}</td>
                        <td className="px-4 py-3 text-right text-slate-600 tabular-nums">{p.unique_sessions}</td>
                      </tr>
                    ))}
                    {!attribution?.top_paths?.length && (
                      <tr><td colSpan={3} className="px-4 py-8 text-center text-slate-400">Sem dados no período.</td></tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="utm">
            <Card>
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-3">Source</th>
                      <th className="text-left px-4 py-3">Medium</th>
                      <th className="text-left px-4 py-3">Campanha</th>
                      <th className="text-right px-4 py-3">Views</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(attribution?.top_utm || []).map((u, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-4 py-3"><Badge variant="secondary">{u.source || '—'}</Badge></td>
                        <td className="px-4 py-3 text-slate-600">{u.medium || '—'}</td>
                        <td className="px-4 py-3 text-slate-600">{u.campaign || '—'}</td>
                        <td className="px-4 py-3 text-right font-semibold tabular-nums">{u.views}</td>
                      </tr>
                    ))}
                    {!attribution?.top_utm?.length && (
                      <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">Sem visitas com parâmetros UTM ainda. Partilha links com <code>?utm_source=…&amp;utm_campaign=…</code>.</td></tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="referrers">
            <Card>
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-3">Referrer</th>
                      <th className="text-right px-4 py-3">Views</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(attribution?.top_referrers || []).map((r, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-4 py-3 truncate max-w-md" title={r.referrer}>
                          <a href={r.referrer} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline inline-flex items-center gap-1">
                            {r.referrer} <ExternalLink className="h-3 w-3" />
                          </a>
                        </td>
                        <td className="px-4 py-3 text-right font-semibold tabular-nums">{r.views}</td>
                      </tr>
                    ))}
                    {!attribution?.top_referrers?.length && (
                      <tr><td colSpan={2} className="px-4 py-8 text-center text-slate-400">Sem referrers externos no período.</td></tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="cohorts">
            <Card>
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-3">Semana</th>
                      <th className="text-right px-4 py-3">Leads</th>
                      <th className="text-right px-4 py-3">Demo enviada</th>
                      <th className="text-right px-4 py-3">HubSpot OK</th>
                      <th className="text-right px-4 py-3">Conversão demo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(cohorts?.cohorts || []).map((c) => {
                      const conv = c.leads > 0 ? Math.round((c.demo_sent / c.leads) * 100) : 0;
                      return (
                        <tr key={c.week} className="hover:bg-slate-50">
                          <td className="px-4 py-3 font-medium">{c.week}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{c.leads}</td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-600">{c.demo_sent}</td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-600">{c.hubspot_ok}</td>
                          <td className="px-4 py-3 text-right">
                            <Badge variant="secondary" className={conv >= 30 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : ''}>
                              {conv}%
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                    {!cohorts?.cohorts?.length && (
                      <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Ainda sem coortes registados.</td></tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Insights Leaderboard — Spark */}
        <Card className="border-indigo-100 bg-gradient-to-br from-indigo-50/40 to-white" data-testid="insights-leaderboard-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Award className="h-4 w-4 text-indigo-600" /> Leaderboard de Insights
              <Badge variant="secondary" className="ml-1 bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px]">
                <Sparkles className="h-2.5 w-2.5 mr-1" />Novo
              </Badge>
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              Artigos com maior tração — <span className="font-medium">conversão</span> = % de sessões que também visitaram uma landing/trust page.
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-3">#</th>
                  <th className="text-left px-4 py-3">Artigo</th>
                  <th className="text-right px-4 py-3">Views</th>
                  <th className="text-right px-4 py-3">Sessões</th>
                  <th className="text-right px-4 py-3">→ Landing</th>
                  <th className="text-right px-4 py-3">Conversão</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(leaderboard?.rows || []).map((row, i) => (
                  <tr key={row.slug} className="hover:bg-slate-50" data-testid={`leaderboard-row-${row.slug}`}>
                    <td className="px-4 py-3 text-slate-400 font-mono">{i + 1}</td>
                    <td className="px-4 py-3">
                      <Link to={`/insights/${row.slug}`} target="_blank" className="text-slate-800 hover:text-indigo-600 font-medium inline-flex items-center gap-1">
                        {row.title} <ExternalLink className="h-3 w-3 text-slate-400" />
                      </Link>
                      {row.status !== 'published' && (
                        <Badge variant="secondary" className="ml-2 text-[10px] bg-amber-50 text-amber-700 border-amber-200">{row.status || 'draft'}</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold tabular-nums">{row.views}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-600">{row.unique_sessions}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-600">{row.funnel_sessions}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`text-sm font-bold ${row.conversion_rate >= 20 ? 'text-emerald-600' : row.conversion_rate >= 5 ? 'text-amber-600' : 'text-slate-500'}`}>
                        {row.conversion_rate}%
                      </span>
                    </td>
                  </tr>
                ))}
                {!leaderboard?.rows?.length && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                    Sem tráfego a insights no período. Publica artigos em <Link to="/settings/insights" className="text-indigo-600 hover:underline">/settings/insights</Link>.
                  </td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* A/B Test — Landing (nis2 / ai-act) with UTM + device breakdown */}
        <Card data-testid="ab-test-card">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  A/B Test · {abTest?.landing_label || 'Landing'}
                  {abTest?.winner && (
                    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]" variant="secondary">
                      Vencedor: {abTest.winner}
                    </Badge>
                  )}
                  {!abTest?.sample_size_ok && (
                    <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-[10px]" variant="secondary">
                      Amostra insuficiente (&lt;30 leads)
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription className="text-xs mt-1">
                  A: copy actual · B: benefit-driven. Atribuído 50/50 no primeiro acesso (sticky por browser). Podes forçar com <code className="bg-slate-100 px-1 rounded">?ab=A</code> ou <code className="bg-slate-100 px-1 rounded">?ab=B</code>.
                </CardDescription>
              </div>
              <Select value={abLanding} onValueChange={setAbLanding}>
                <SelectTrigger className="w-44" data-testid="ab-landing-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="nis2">Landing NIS2</SelectItem>
                  <SelectItem value="ai-act">Landing AI Act</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {['A', 'B'].map(v => {
                const d = abTest?.variants?.[v] || {};
                const isWinner = abTest?.winner === v;
                return (
                  <div key={v} className={`rounded-lg border p-4 ${isWinner ? 'border-emerald-300 bg-emerald-50/40' : 'border-slate-200 bg-white'}`} data-testid={`ab-variant-${v}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-bold text-slate-900">Variante {v}</span>
                      <span className="text-xs text-slate-500 uppercase tracking-wide">{v === 'A' ? 'Control' : 'Variante'}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div>
                        <div className="text-[10px] text-slate-500 uppercase">Views</div>
                        <div className="text-xl font-bold tabular-nums">{d.views ?? 0}</div>
                      </div>
                      <div>
                        <div className="text-[10px] text-slate-500 uppercase">Leads</div>
                        <div className="text-xl font-bold tabular-nums">{d.leads ?? 0}</div>
                      </div>
                      <div>
                        <div className="text-[10px] text-slate-500 uppercase">Conv.</div>
                        <div className={`text-xl font-bold tabular-nums ${d.conversion_pct >= 5 ? 'text-emerald-600' : 'text-slate-700'}`}>{d.conversion_pct ?? 0}%</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-3 flex items-center justify-between text-xs text-slate-500 flex-wrap gap-2">
              <span>Total leads: <strong className="tabular-nums text-slate-800">{abTest?.total_leads ?? 0}</strong></span>
              <span>Lift B vs A: <strong className={`tabular-nums ${(abTest?.lift_pct_points ?? 0) > 0 ? 'text-emerald-700' : (abTest?.lift_pct_points ?? 0) < 0 ? 'text-red-600' : 'text-slate-600'}`}>{abTest?.lift_pct_points ?? 0} pp</strong></span>
              {abTest?.unassigned?.leads ? <span>Não atribuídos: <strong className="tabular-nums">{abTest.unassigned.leads}</strong></span> : null}
            </div>

            {/* Breakdown tables */}
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div data-testid="ab-breakdown-utm">
                <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Por origem UTM</div>
                <div className="rounded-lg border border-slate-200 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide text-[10px]"><tr>
                      <th className="px-3 py-2 text-left">Origem</th>
                      <th className="px-3 py-2 text-right">A conv.</th>
                      <th className="px-3 py-2 text-right">B conv.</th>
                      <th className="px-3 py-2 text-right">Lift</th>
                    </tr></thead>
                    <tbody className="divide-y divide-slate-100">
                      {(abTest?.breakdown?.by_utm_source || []).slice(0, 8).map(row => (
                        <tr key={row.utm_source} data-testid={`ab-utm-row-${row.utm_source}`}>
                          <td className="px-3 py-2 text-slate-700 font-medium">{row.utm_source}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            <div>{row.A.conversion_pct}%</div>
                            <div className="text-[10px] text-slate-400">{row.A.leads}/{row.A.views}</div>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            <div>{row.B.conversion_pct}%</div>
                            <div className="text-[10px] text-slate-400">{row.B.leads}/{row.B.views}</div>
                          </td>
                          <td className={`px-3 py-2 text-right tabular-nums font-semibold ${row.lift_pct_points > 0 ? 'text-emerald-700' : row.lift_pct_points < 0 ? 'text-red-600' : 'text-slate-500'}`}>
                            {row.lift_pct_points > 0 ? '+' : ''}{row.lift_pct_points} pp
                          </td>
                        </tr>
                      ))}
                      {!(abTest?.breakdown?.by_utm_source || []).length && (
                        <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400">Sem dados por origem no período.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div data-testid="ab-breakdown-device">
                <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Por dispositivo</div>
                <div className="rounded-lg border border-slate-200 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide text-[10px]"><tr>
                      <th className="px-3 py-2 text-left">Dispositivo</th>
                      <th className="px-3 py-2 text-right">A conv.</th>
                      <th className="px-3 py-2 text-right">B conv.</th>
                      <th className="px-3 py-2 text-right">Lift</th>
                    </tr></thead>
                    <tbody className="divide-y divide-slate-100">
                      {(abTest?.breakdown?.by_device || []).map(row => (
                        <tr key={row.device} data-testid={`ab-device-row-${row.device}`}>
                          <td className="px-3 py-2 text-slate-700 font-medium capitalize">{row.device}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            <div>{row.A.conversion_pct}%</div>
                            <div className="text-[10px] text-slate-400">{row.A.leads}/{row.A.views}</div>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            <div>{row.B.conversion_pct}%</div>
                            <div className="text-[10px] text-slate-400">{row.B.leads}/{row.B.views}</div>
                          </td>
                          <td className={`px-3 py-2 text-right tabular-nums font-semibold ${row.lift_pct_points > 0 ? 'text-emerald-700' : row.lift_pct_points < 0 ? 'text-red-600' : 'text-slate-500'}`}>
                            {row.lift_pct_points > 0 ? '+' : ''}{row.lift_pct_points} pp
                          </td>
                        </tr>
                      ))}
                      {!(abTest?.breakdown?.by_device || []).length && (
                        <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400">Sem dados por dispositivo no período.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
